import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Modal from '@/components/common/Modal';
import AiInstructionForm from './AiInstructionForm';
import { Brain, AlertCircle, Edit, Trash2, Plus } from 'lucide-react';
import { useAiInstructions, useUpdateAiInstruction, useDeleteAiInstruction, useCreateAiInstruction } from '@/hooks/useAiInstructions';

const SCOPES = [
  { value: 'global', label: 'Global', description: 'Sent with every AI request' },
  { value: 'secretary', label: 'Secretary', description: 'Digest & brief generation' },
  { value: 'analysis', label: 'Analysis', description: 'Email categorization' },
  { value: 'create_event', label: 'Events', description: 'Event creation guidance' },
  { value: 'pending_reply', label: 'Replies', description: 'Reply detection guidance' },
  { value: 'custom', label: 'Chat', description: 'Boswell chat conversations' }
];

export default function AiInstructionsSection() {
  const [activeScope, setActiveScope] = useState('global');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState(null);
  const [expandedInstructions, setExpandedInstructions] = useState(new Set());

  const { data: instructions, isLoading, error } = useAiInstructions(activeScope, null);
  const createMutation = useCreateAiInstruction();
  const updateMutation = useUpdateAiInstruction();
  const deleteMutation = useDeleteAiInstruction();

  const handleCreate = () => {
    setEditingInstruction(null);
    setIsModalOpen(true);
  };

  const handleEdit = (instruction) => {
    setEditingInstruction(instruction);
    setIsModalOpen(true);
  };

  const handleDelete = async (instruction) => {
    if (instruction.is_system) {
      return; // Should not happen due to disabled button
    }

    if (confirm(`Are you sure you want to delete "${instruction.display_name}"?`)) {
      deleteMutation.mutate(instruction.id);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingInstruction) {
        await updateMutation.mutateAsync({ id: editingInstruction.id, updates: formData });
      } else {
        await createMutation.mutateAsync(formData);
        // Switch to the created instruction's scope so the user sees it
        if (formData.scope && formData.scope !== activeScope) {
          setActiveScope(formData.scope);
        }
      }
      setIsModalOpen(false);
      setEditingInstruction(null);
    } catch (error) {
      console.error('Failed to save instruction:', error);
    }
  };

  const handleToggleEnabled = async (instruction) => {
    if (instruction.is_system) {
      return; // Should not happen due to disabled toggle
    }

    updateMutation.mutate({
      id: instruction.id,
      updates: { enabled: !instruction.enabled }
    });
  };

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedInstructions);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedInstructions(newExpanded);
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const isProcessing = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            AI Instructions
          </CardTitle>
          <CardDescription>
            Configure how AI processes and analyzes your emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Scope Tabs */}
          <div className="flex gap-2 mb-6 border-b">
            {SCOPES.map((scope) => (
              <button
                key={scope.value}
                onClick={() => setActiveScope(scope.value)}
                title={scope.description}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeScope === scope.value
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {scope.label}
              </button>
            ))}
          </div>

          {/* Instructions List */}
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">Failed to load instructions</p>
            </div>
          ) : !instructions || instructions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No instructions for this scope
            </div>
          ) : (
            <div className="space-y-3 mb-4">
              {instructions.map((instruction) => {
                const isExpanded = expandedInstructions.has(instruction.id);
                const showExpandButton = instruction.instruction_text && instruction.instruction_text.length > 150;

                return (
                  <div key={instruction.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-medium text-sm">{instruction.display_name}</h4>

                          {instruction.is_system && (
                            <Badge variant="secondary" className="text-xs">
                              System
                            </Badge>
                          )}

                          <Badge variant="outline" className="text-xs">
                            Priority: {instruction.priority}
                          </Badge>

                          <Badge
                            variant={instruction.enabled ? 'success' : 'secondary'}
                            className="text-xs"
                          >
                            {instruction.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>

                        <p className="text-sm text-muted-foreground mt-2">
                          {isExpanded ? instruction.instruction_text : truncateText(instruction.instruction_text)}
                        </p>

                        {showExpandButton && (
                          <button
                            onClick={() => toggleExpanded(instruction.id)}
                            className="text-xs text-primary hover:underline mt-1"
                          >
                            {isExpanded ? 'Show less' : 'Show more'}
                          </button>
                        )}

                        {instruction.created_by && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Created by: {instruction.created_by}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleEnabled(instruction)}
                          disabled={instruction.is_system || isProcessing}
                          className={`px-3 py-1.5 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            instruction.enabled
                              ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-950 dark:text-green-400 dark:hover:bg-green-900'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                          }`}
                          title={instruction.is_system ? 'Cannot modify system instructions' : (instruction.enabled ? 'Disable' : 'Enable')}
                        >
                          {instruction.enabled ? 'ON' : 'OFF'}
                        </button>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(instruction)}
                          disabled={instruction.is_system || isProcessing}
                          className="p-2"
                          title={instruction.is_system ? 'Cannot edit system instructions' : 'Edit instruction'}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(instruction)}
                          disabled={instruction.is_system || isProcessing}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                          title={instruction.is_system ? 'Cannot delete system instructions' : 'Delete instruction'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Instruction Button */}
          <div className="border-t pt-4">
            <Button
              onClick={handleCreate}
              disabled={isProcessing}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Instruction
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingInstruction(null);
        }}
        title={editingInstruction ? 'Edit Instruction' : 'Create Instruction'}
        size="lg"
      >
        <AiInstructionForm
          instruction={editingInstruction}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingInstruction(null);
          }}
          isLoading={isProcessing}
        />
      </Modal>
    </>
  );
}
